import { uploadUserAudioFiles } from "../api/WebAPI";
import { MainStore } from "../store";
import { genNewId } from "./ids";

/**
 * Removes all uploaded audio regions (identified by "blob:" in their `audioPath`)
 * from the provided editor data store. This includes cleaning up associated regions,
 * lanes, and lane IDs to ensure the data structure remains consistent.
 *
 * @param editorState - The full editor redux store.
 * @returns The updated editor data store with uploaded audio regions and empty lanes removed.
 *
 * ### Steps:
 * 1. **Remove Regions**: Filters out regions where the `audioPath` starts with "blob:"
 *    and collects their IDs.
 * 2. **Remove WaveformBuffers**: Cleans up the waveform buffers by removing entries
 *    associated with the removed regions.
 * 3. **Update Lanes**: Removes references to the removed region IDs from lanes. If a
 *    lane becomes empty (i.e., has no regions), it is also removed.
 * 4. **Clean Lane IDs**: Updates the `lanes.laneIds` array to exclude IDs of removed lanes.
 */
export const removeUploadedAudioFromEditorState = (
  editorState: MainStore
): MainStore => {
  // Step 1: Remove regions with "blob:" in audioPath
  const removedRegionIds = new Set<string>();
  editorState.project.regions.byId = Object.keys(
    editorState.project.regions.byId
  )
    .filter(regionId => {
      const region = editorState.project.regions.byId[regionId];
      if (region.audioPath.startsWith("blob:")) {
        removedRegionIds.add(regionId);
        return false;
      }
      return true;
    })
    .reduce((acc, regionId) => {
      acc[regionId] = editorState.project.regions.byId[regionId];
      return acc;
    }, {});

  // Step 2: Remove waveform buffers with "blob:" in audioPath
  Object.keys(editorState.waveformBuffers.byAudioPath).forEach(audioPath => {
    if (audioPath.startsWith("blob:")) {
      delete editorState.waveformBuffers.byAudioPath[audioPath];
    }
  });

  // Step 3: Remove regionIds from lanes and track empty lanes
  const removedLaneIds = new Set<string>();
  editorState.project.lanes.byId = Object.keys(editorState.project.lanes.byId)
    .filter(laneId => {
      const lane = editorState.project.lanes.byId[laneId];
      lane.regionIds = lane.regionIds.filter(
        regionId => !removedRegionIds.has(regionId)
      );
      if (lane.regionIds.length === 0) {
        removedLaneIds.add(laneId);
        return false;
      }
      return true;
    })
    .reduce((acc, laneId) => {
      acc[laneId] = editorState.project.lanes.byId[laneId];
      return acc;
    }, {});

  // Step 4: Remove empty lanes from lanes.laneIds
  editorState.project.lanes.laneIds = editorState.project.lanes.laneIds.filter(
    laneId => !removedLaneIds.has(laneId)
  );

  return editorState;
};

/**
 * Downloads all uploaded audio files referenced in regions (identified by "UserAudioFiles/"
 * in their `audioPath`) and replaces the audio paths with Blob URLs for local use.
 *
 * @param editorState
 * @returns editorState with audio paths replaced by Blob URLs
 */
export const userAudioFilesToBlob = async (
  editorState: MainStore
): Promise<MainStore> => {
  const serverPaths = new Set<string>();
  Object.values(editorState.project.regions.byId).forEach(region => {
    if (region.audioPath.startsWith("UserAudioFiles/")) {
      serverPaths.add(region.audioPath);
    }
  });
  if (serverPaths.size === 0) return editorState;

  const pathToBlobUrl = new Map<string, string>();

  for (const serverPath of serverPaths) {
    // these are public so we can just get them directly
    const fullS3Url = `${process.env.REACT_APP_FILES_URL}/${serverPath}`;
    let resp: Response | null = null;
    try {
      resp = await fetch(fullS3Url);
    } catch {
      resp = null;
    }
    if (!resp || !resp.ok) {
      // could not fetch this file, skip it
      // this will keep the old server path intact
      // note this is potentially problematic if the original CustomMix is deleted
      // but it's a reasonable fallback
      continue;
    }

    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    pathToBlobUrl.set(serverPath, blobUrl);

    // Move any waveform buffer keyed by the original server path to the blob URL
    if (editorState.waveformBuffers.byAudioPath[serverPath]) {
      editorState.waveformBuffers.byAudioPath[blobUrl] =
        editorState.waveformBuffers.byAudioPath[serverPath];
      delete editorState.waveformBuffers.byAudioPath[serverPath];
    }
  }

  // Replace region paths with blob URLs (reuse same blob for duplicate server paths)
  Object.values(editorState.project.regions.byId).forEach(region => {
    if (region.audioPath.startsWith("UserAudioFiles/")) {
      const mapped = pathToBlobUrl.get(region.audioPath);
      if (mapped) {
        region.audioPath = mapped;
        region.audioPathHQ = mapped;
        region.audioPathWav = mapped;
      }
    }
  });

  return editorState;
};

/**
 * Uploads all local audio blobs referenced in regions (identified by "blob:" in their `audioPath`)
 * from the provided editor data store to s3, then updates the audio paths to their s3 link
 * counterparts.
 *
 * @param editorState - The full editor redux store.
 * @returns The updated editor data store with local audio blobs replaced with s3 urls.
 *
 * ### Steps:
 * 1. Collect all local audio paths referencing blob files
 * 2. Reconstruct files from blob URLs and upload them to s3
 * 3. Replace blob URLs with s3 links in regions
 * 4. Replace blob URLs with s3 links in waveform buffers (envelopes)
 */
export const uploadAudioToServerAndUseLinks = async (
  editorState: MainStore
): Promise<MainStore> => {
  // Step 1: Collect all local blob URLs
  const blobUrls = new Set<string>();
  Object.values(editorState.project.regions.byId).forEach(region => {
    if (region.audioPath.startsWith("blob:")) {
      blobUrls.add(region.audioPath);
    }
  });
  if (blobUrls.size === 0) return editorState;

  // Step 2: Reconstruct files from blob URLs and upload them to the backend server
  const filesToUpload: File[] = [];
  for (const blobUrl of blobUrls) {
    const uuid = blobUrl.split("/").pop() || genNewId();
    const file = await reconstructFileFromObjectURL(
      blobUrl,
      `${uuid}.wav`,
      "audio/wav"
    );
    filesToUpload.push(file);
  }
  const customMixId = editorState.project.customMixInfo.id;
  const uploadedUrls = await uploadUserAudioFiles(filesToUpload, customMixId);

  // Step 3: Replace blob URLs with uploaded s3 link in regions
  Object.values(editorState.project.regions.byId).forEach(region => {
    if (region.audioPath.startsWith("blob:")) {
      const uploadUUID = region.audioPath.split("/").pop();
      let s3Url = uploadedUrls[`${uploadUUID}.wav`];
      s3Url = s3Url
        .replace("https://", "")
        .split("/")
        .slice(1)
        .join("/");
      region.audioPath = s3Url;
      region.audioPathHQ = s3Url;
      region.audioPathWav = s3Url;
    }
  });

  // Step 4: Replace blob URLs with uploaded s3 link in waveform buffers
  Object.keys(editorState.waveformBuffers.byAudioPath).forEach(audioPath => {
    if (audioPath.startsWith("blob:")) {
      const uploadUUID = audioPath.split("/").pop();
      let s3Url = uploadedUrls[`${uploadUUID}.wav`];
      s3Url = s3Url
        .replace("https://", "")
        .split("/")
        .slice(1)
        .join("/");
      editorState.waveformBuffers.byAudioPath[s3Url] =
        editorState.waveformBuffers.byAudioPath[audioPath];
      delete editorState.waveformBuffers.byAudioPath[audioPath];
    }
  });

  return editorState;
};

/*
 * Reconstruct a File object from an Object URL
 * @param {string} objectURL - The Object URL to reconstruct the file from
 * @param {string} originalFileName - The original file name
 * @param {string} originalFileType - The original file type (MIME type)
 * @returns {Promise<File>} - A promise that resolves to the reconstructed File object
 */
export const reconstructFileFromObjectURL = async (
  objectURL: string,
  originalFileName: string,
  originalFileType: string
): Promise<File> => {
  const response = await fetch(objectURL);
  const blob = await response.blob();

  const reconstructedFile = new File([blob], originalFileName, {
    type: originalFileType
  });

  return reconstructedFile;
};
